import { CloseOutlined, DeleteOutlined, EditOutlined } from "@ant-design/icons";
import { Button, Flex, notification, Popconfirm } from "antd";
import dayjs from "dayjs";
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { deleteContractDeleteThunk, getNetworkInfoThunk } from "tm-shared/api";
import { Contract as ContractType, UserRole } from "tm-shared/types";

import Header from "@/components/Header";
import { Loading } from "@/components/Loading";
import Meta from "@/components/Meta";
import { PrimaryText } from "@/components/Typography/Styled";
import { useAppDispatch, useAppSelector } from "@/hooks/redux";
import { useMarket } from "@/hooks/useMarket";
import AddContract from "@/pages/WorkPlan/AboutPoint/InfoTab/AddContract";

import { StyledAPContainer } from "../../../Styled";
import { ContractView } from "./ContractView";

const isDateTodayOrAfter = (date?: string | Date | null): boolean => {
    if (!date) return true;

    const input = dayjs(date);
    const today = dayjs().startOf("day");

    return input.isSame(today, "day") || input.isAfter(today, "day");
};

const Contract = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const { marketId, contractId } = useParams<{
        marketId: string;
        contractId: string;
    }>();

    const { contractTypesList, targetsContracts } = useAppSelector(
        (state) => state.offline,
    );
    const { role, networks } = useAppSelector((state) => state.user);

    const [editMode, setEditMode] = useState<boolean>(false);

    if (!marketId || !contractId) {
        throw new Error("Route params are missing");
    }

    const { market, marketLoading } = useMarket(marketId);

    const contract = useMemo<ContractType | undefined>(() => {
        if (!market || !targetsContracts) return undefined;

        const contractSources = [
            marketId,
            market.storage?.id,
            market.contragent?.id,
            market.address?.mainRegionId,
            market.networkId,
        ].filter(Boolean);

        for (const sourceId of contractSources) {
            const sourceContracts = targetsContracts[sourceId!];
            if (sourceContracts) {
                const foundContract = Object.values(sourceContracts).find(
                    (item: ContractType) => item.id === contractId,
                );
                if (foundContract) {
                    return foundContract;
                }
            }
        }

        return undefined;
    }, [market, targetsContracts, marketId, contractId]);

    const handleDeleteContract = async () => {
        try {
            await dispatch(deleteContractDeleteThunk({ contractId })).unwrap();

            navigate(-1);

            notification.success({
                message: "Контракт видалений",
            });
        } catch (error) {
            console.error("Error deleting contract:", error);
            notification.error({
                message: "Помилка при видаленні контракту",
            });
        }
    };

    const handleCancelEdit = () => {
        setEditMode(false);
    };

    const handleStartEdit = () => {
        setEditMode(true);
    };

    useEffect(() => {
        if (market?.networkId && !networks?.[market.networkId]) {
            dispatch(getNetworkInfoThunk({ network: market.networkId }));
        }
    }, [contract, navigate]);

    if (marketLoading) return <Loading />;

    if (!contract) {
        return (
            <>
                <Meta title="Помилка" />
                <Header title="Контракт не знайдено" back titleFontSize={16} />
                <StyledAPContainer>
                    <PrimaryText
                        fontSize={16}
                        fontWeight="400"
                        style={{ textAlign: "center" }}
                    >
                        Контракт не знайдено або видалено.
                    </PrimaryText>
                </StyledAPContainer>
            </>
        );
    }

    const fopName = market?.contragent?.name ?? "Невідомий ринок";
    const contractTypeName =
        contractTypesList?.[contract.type] ?? "Невідомий вид контракту";
    const isSupervisor = role === UserRole.Sv;
    const canEdit = !editMode && isDateTodayOrAfter(contract.endDate);

    return (
        <>
            <Meta title="TT Info Contract" />
            <Header
                title={contract.name}
                back
                titleFontSize={16}
                rightActions={
                    isSupervisor ? (
                        <Flex gap={4}>
                            {canEdit && (
                                <Button
                                    type="text"
                                    icon={<EditOutlined />}
                                    onClick={handleStartEdit}
                                    aria-label="Редагувати контракт"
                                />
                            )}

                            {editMode && (
                                <Button
                                    type="text"
                                    icon={<CloseOutlined />}
                                    onClick={handleCancelEdit}
                                    aria-label="Скасувати редагування"
                                />
                            )}

                            <Popconfirm
                                title="Видалити контракт"
                                description="Ви впевнені що хочете видалити контракт?"
                                onConfirm={handleDeleteContract}
                                okText="Так"
                                cancelText="Ні"
                            >
                                <Button
                                    type="text"
                                    icon={<DeleteOutlined />}
                                    aria-label="Видалити контракт"
                                />
                            </Popconfirm>
                        </Flex>
                    ) : null
                }
            />
            <StyledAPContainer>
                {editMode ? (
                    <AddContract editMode contract={contract} fop={fopName} />
                ) : (
                    <ContractView
                        contract={contract}
                        fopName={fopName}
                        contractTypeName={contractTypeName}
                        market={market}
                    />
                )}
            </StyledAPContainer>
        </>
    );
};

export default Contract;
